import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Link } from "wouter";
import { ArrowRight, ArrowLeft, Star, MapPin, CalendarClock, Truck, Sparkles } from "lucide-react";
import { ReviewList } from "@/components/ReviewList";
import { useServices } from "@/hooks/use-services";
import { usePublishedReviews } from "@/hooks/use-reviews";
import { useTranslation } from "react-i18next";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { cn } from "@/lib/utils";

type PricedVehicle = 'sedan' | 'suv' | 'cuv' | 'motorcycle';
type MenuVehicle = PricedVehicle | 'all';

/** Worded eyebrow — labels each section (meaning, not decoration). */
function Eyebrow({ children, className }: { children: React.ReactNode; className?: string }) {
  return <span className={cn("u-eyebrow text-primary", className)}>{children}</span>;
}

export default function Home() {
  const { data: services } = useServices();
  const { data: allReviews } = usePublishedReviews();
  const avgRating = allReviews && allReviews.length > 0
    ? allReviews.reduce((sum, r) => sum + (r.rating || 0), 0) / allReviews.length
    : 0;
  const reviewCount = allReviews?.length || 0;
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const Arrow = isRTL ? ArrowLeft : ArrowRight;
  const currency = isRTL ? "د.ك" : "KD";
  const L = (en?: string, ar?: string) => (isRTL ? ar : en) ?? "";

  // Packages (non-add-ons), ascending by index — a real tier sequence.
  const packages = useMemo(
    () => (services ?? []).filter(s => !s.isAddOn).sort((a, b) => (a.index ?? 0) - (b.index ?? 0)),
    [services],
  );

  // Which vehicle prices exist across the menu (sedan / cuv / suv / motorcycle).
  const vehicles = useMemo(() => {
    const order: { value: PricedVehicle; label: string }[] = [
      { value: 'sedan', label: t("booking.sedan") },
      { value: 'cuv', label: t("booking.cuv") },
      { value: 'suv', label: t("booking.suv") },
      { value: 'motorcycle', label: t("booking.motorcycle") },
    ];
    return order.filter(v => packages.some(p => (p.vehiclePricing?.[v.value] ?? 0) > 0));
  }, [packages, t]);

  // Toggle options: "All" (every vehicle's price per package) plus each priced vehicle.
  const vehicleOptions = useMemo<{ value: MenuVehicle; label: string }[]>(
    () => (vehicles.length > 0 ? [{ value: 'all', label: t("booking.allVehicles") }, ...vehicles] : []),
    [vehicles, t],
  );

  const [vehicle, setVehicle] = useState<MenuVehicle>('all');

  // Single-vehicle mode hides packages with no price for that vehicle (0/missing
  // in the table). Legacy packages without a vehicle table always show.
  const visiblePackages = useMemo(
    () => vehicle === 'all'
      ? packages
      : packages.filter(p => !p.vehiclePricing || (p.vehiclePricing[vehicle] ?? 0) > 0),
    [packages, vehicle],
  );

  const steps = t("home.how.steps", { returnObjects: true }) as { t: string; d: string }[];
  const stepIcons = [CalendarClock, Truck, Sparkles];
  const marquee = t("home.marquee", { returnObjects: true }) as string[];

  return (
    <div className="min-h-screen overflow-x-hidden">
      {/* ── Hero: the wordmark is the thesis (van-livery treatment) ── */}
      <section className="hero-navy relative overflow-hidden text-white">
        <div className="hero-ghost" aria-hidden>
          <span>Crystal</span>
        </div>
        <div className="hero-sweep-band" aria-hidden />

        <div className="container relative z-10 mx-auto px-4 py-24 lg:py-36 xl:py-44">
          <div className="max-w-3xl mx-auto text-center animate-fade-in-up">
            <img
              src="/brand/crystal-wash-icon-white.png"
              alt="Crystal Wash"
              className="mx-auto mb-6 h-24 w-auto md:h-36"
            />
            <Eyebrow className="text-[#8FB6D3]">{t("hero.eyebrow")}</Eyebrow>

            <h1 className="u-serif mt-5 mb-6 text-[3rem] leading-[1.02] md:text-7xl lg:text-[5.25rem] tracking-tight text-white">
              <span className="block font-semibold">{t("hero.slogan1")}</span>
              <span className="block italic font-medium text-[#BFE0F5]">{t("hero.slogan2")}</span>
            </h1>

            <p className="text-base md:text-lg leading-relaxed text-white/70 max-w-lg mx-auto mb-9">
              {t("hero.description")}
            </p>

            <div className="flex flex-wrap gap-3 justify-center">
              <Link href="/booking">
                <Button size="lg" className="px-7 bg-white text-[#0C2242] hover:bg-[#BFE0F5] hover:text-[#0C2242] shadow-lg shadow-black/20 font-semibold" data-testid="button-hero-book">
                  {t("hero.bookNow")}
                  <Arrow className="ms-2 w-4 h-4" />
                </Button>
              </Link>
              <Link href="/services">
                <Button size="lg" variant="outline" className="px-7 border-white/25 bg-transparent text-white hover:bg-white/10 hover:text-white">
                  {t("hero.ourServices")}
                </Button>
              </Link>
            </div>

            <div className="mt-12 flex flex-wrap items-center justify-center gap-x-6 gap-y-3">
              {avgRating > 0 && (
                <div className="flex items-center gap-2">
                  <div className="flex">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <Star key={s} className={`h-4 w-4 ${s <= Math.round(avgRating) ? "fill-[#BFE0F5] text-[#BFE0F5]" : "text-white/25"}`} />
                    ))}
                  </div>
                  <span className="text-sm font-semibold">{avgRating.toFixed(1)}</span>
                  <span className="text-sm text-white/50">
                    {reviewCount} {reviewCount === 1 ? t("reviews.single") : t("reviews.plural")}
                  </span>
                </div>
              )}
              <span className="hidden sm:block h-4 w-px bg-white/20" />
              <div className="flex items-center gap-2 text-sm text-white/70">
                <MapPin className="h-4 w-4 text-[#8FB6D3]" />
                {t("hero.doorstep")}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Livery marquee ribbon ─────────────────────────────────── */}
      <div className="cw-marquee bg-primary text-primary-foreground py-3 select-none" aria-hidden>
        <div className="cw-marquee__track">
          {[...marquee, ...marquee].map((word, i) => (
            <span key={i} className="u-eyebrow inline-flex items-center text-primary-foreground/90">
              <span className="px-6">{word}</span>
              <span className="text-[#BFE0F5]">✦</span>
            </span>
          ))}
        </div>
      </div>

      {/* ── How it works — a real doorstep sequence ───────────────── */}
      <section className="py-24 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mb-16">
            <Eyebrow>{t("home.how.eyebrow")}</Eyebrow>
            <h2 className="u-serif text-3xl md:text-[2.7rem] leading-tight font-semibold mt-3 text-foreground">
              {t("home.how.title")}
            </h2>
          </div>
          <ol className="grid md:grid-cols-3 gap-x-8 gap-y-12">
            {steps.map((step, i) => {
              const Icon = stepIcons[i] ?? Sparkles;
              return (
                <li key={i} className="relative">
                  <div className="flex items-center gap-4 mb-5">
                    <span className="u-eyebrow text-primary/60 text-base">0{i + 1}</span>
                    <span className="flex-1 h-px bg-border" />
                    <span className="w-11 h-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                      <Icon className="w-5 h-5" />
                    </span>
                  </div>
                  <h3 className="text-xl font-bold mb-2 text-foreground">{step.t}</h3>
                  <p className="text-muted-foreground leading-relaxed">{step.d}</p>
                </li>
              );
            })}
          </ol>
        </div>
      </section>

      {/* ── The Menu — packages as an editorial price list ────────── */}
      <section className="py-24 bg-muted/40 border-y border-border">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10">
            <div>
              <Eyebrow>{t("home.menu.eyebrow")}</Eyebrow>
              <h2 className="u-serif text-3xl md:text-[2.7rem] leading-tight font-semibold mt-3 text-foreground">
                {t("home.menu.title")}
              </h2>
            </div>
            {/* Vehicle price toggle */}
            {vehicleOptions.length > 0 && (
              <div
                role="group"
                aria-label={t("booking.vehicleType")}
                className="flex flex-wrap gap-2 self-start lg:justify-end"
              >
                {vehicleOptions.map(v => (
                  <button
                    key={v.value}
                    type="button"
                    onClick={() => setVehicle(v.value)}
                    aria-pressed={vehicle === v.value}
                    className={cn(
                      "whitespace-nowrap rounded-full border px-4 py-1.5 text-sm font-medium transition-colors",
                      vehicle === v.value
                        ? "border-primary bg-primary text-primary-foreground shadow-sm"
                        : "border-border bg-card text-muted-foreground hover:border-primary/50 hover:text-foreground",
                    )}
                    data-testid={`menu-vehicle-${v.value}`}
                  >
                    {v.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          <ul className="divide-y divide-border border-y border-border">
            {visiblePackages.map((s, i) => {
              const price = vehicle === 'all' ? s.price : (s.vehiclePricing?.[vehicle] ?? s.price);
              // In "All" mode, list every vehicle this package has a price for.
              const allPrices = vehicle === 'all'
                ? vehicles
                    .map(v => ({ ...v, price: s.vehiclePricing?.[v.value] ?? 0 }))
                    .filter(v => v.price > 0)
                : [];
              // Deep-link into booking with this package (and the chosen vehicle) preselected.
              const bookHref = vehicle === 'all'
                ? `/booking?serviceId=${s.id}`
                : `/booking?serviceId=${s.id}&vehicle=${vehicle}`;
              return (
                <li key={s.id}>
                  <Link
                    href={bookHref}
                    className="menu-row group flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6 py-6 transition-colors hover:bg-background/60 -mx-3 px-3 rounded-xl"
                    data-testid={`menu-item-${s.id}`}
                  >
                    <div className="flex items-center gap-4 sm:gap-6 min-w-0 flex-auto">
                      {s.imageUrl
                        ? <img src={s.imageUrl} alt="" className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl object-cover flex-shrink-0 ring-1 ring-border" />
                        : <span className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl bg-primary/10 flex-shrink-0" />}
                      <span className="menu-index u-eyebrow text-muted-foreground text-base transition-colors flex-shrink-0 hidden sm:block">0{i + 1}</span>
                      <div className="min-w-0 flex-1">
                        <h3 className="u-serif text-xl sm:text-2xl font-semibold text-foreground leading-tight">
                          {L(s.nameEn, s.nameAr)}
                        </h3>
                        <p className="text-sm text-muted-foreground line-clamp-2 mt-1 leading-relaxed">{L(s.descriptionEn, s.descriptionAr)}</p>
                        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                          {s.category && (
                            <span className="inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 uppercase tracking-wide text-primary font-semibold">
                              {s.category}
                            </span>
                          )}
                          <span className="inline-flex items-center gap-1"><CalendarClock className="w-3.5 h-3.5" />{s.duration} {isRTL ? "دقيقة" : "min"}</span>
                        </div>
                      </div>
                    </div>

                    <span className="menu-leader hidden sm:block" aria-hidden />

                    {allPrices.length > 0 ? (
                      // "All" mode: a mini menu-style price list, one dotted line per vehicle.
                      <dl className="w-full sm:w-56 flex-shrink-0 space-y-1" data-testid={`menu-all-prices-${s.id}`}>
                        {allPrices.map(v => (
                          <div key={v.value} className="flex items-baseline gap-2">
                            <dt className="text-xs text-muted-foreground whitespace-nowrap">{v.label}</dt>
                            <span className="flex-1 border-b border-dotted border-border translate-y-[-0.2rem]" aria-hidden />
                            <dd className="font-bold text-foreground tabular-nums whitespace-nowrap text-base sm:text-lg">
                              {v.price} <span className="text-xs font-medium text-muted-foreground">{currency}</span>
                            </dd>
                          </div>
                        ))}
                      </dl>
                    ) : (
                      <div className="flex items-center justify-between sm:flex-col sm:items-end gap-2 flex-shrink-0">
                        <div className="text-2xl sm:text-3xl font-bold text-foreground whitespace-nowrap tabular-nums">
                          {price} <span className="text-sm font-medium text-muted-foreground">{currency}</span>
                        </div>
                        <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary sm:bg-transparent sm:px-0 sm:py-0 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                          {t("hero.bookNow")} <Arrow className="w-3 h-3" />
                        </span>
                      </div>
                    )}
                  </Link>
                </li>
              );
            })}
          </ul>
          <p className="text-sm text-muted-foreground mt-6">{t("home.menu.note")}</p>
        </div>
      </section>

      {/* ── Proof — reviews ───────────────────────────────────────── */}
      <section className="py-24 bg-background">
        <div className="container mx-auto px-4 text-center">
          <Eyebrow>{t("reviews.eyebrow")}</Eyebrow>
          <h2 className="u-serif text-3xl md:text-[2.7rem] font-semibold mt-3 mb-14 text-foreground">
            {t("reviews.title")}
          </h2>
          <ReviewList />
        </div>
      </section>

      {/* ── Questions — FAQ ───────────────────────────────────────── */}
      <section className="py-24 bg-muted/40 border-t border-border">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <Eyebrow>{t("faq.eyebrow")}</Eyebrow>
            <h2 className="u-serif text-3xl md:text-[2.7rem] font-semibold mt-3 text-foreground">
              {t("faq.title")}
            </h2>
          </div>
          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="w-full">
              {(t("faq.items", { returnObjects: true }) as { question: string; answer: string }[]).map((item, index) => (
                <AccordionItem key={index} value={`item-${index}`} className="border-b border-border">
                  <AccordionTrigger className="text-start py-5 hover:no-underline font-semibold text-foreground" data-testid={`faq-question-${index}`}>
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground pb-5 text-[0.95rem] leading-relaxed">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* ── Closing CTA band ──────────────────────────────────────── */}
      <section className="hero-navy relative overflow-hidden text-white">
        <div className="hero-ghost" aria-hidden><span>Wash</span></div>
        <div className="container relative z-10 mx-auto px-4 py-20 lg:py-24 text-center">
          <Eyebrow className="text-[#8FB6D3]">{t("home.cta.eyebrow")}</Eyebrow>
          <h2 className="u-serif text-4xl md:text-6xl font-semibold mt-4 mb-4 text-white">
            {t("home.cta.title")}
          </h2>
          <p className="text-white/70 text-lg max-w-xl mx-auto mb-8">{t("home.cta.body")}</p>
          <Link href="/booking">
            <Button size="lg" className="px-8 bg-white text-[#0C2242] hover:bg-[#BFE0F5] hover:text-[#0C2242] font-semibold shadow-lg shadow-black/20">
              {t("home.cta.button")}
              <Arrow className="ms-2 w-4 h-4" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
